import config from '@/helpers/config'
import ServerError from '@/model/ServerError'
import ServerResponse from '@/model/ServerResponse'
import axios from 'axios'
import { useGlobalStore } from '@/stores/global'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { LocalNotifications } from '@capacitor/local-notifications'
import { saveAs } from 'file-saver'
import { FileOpener } from '@capacitor-community/file-opener'
import permissionsHelper from './PermissionsHelper'

const httpClientWrapper = {
    get: get,
    post: post,
    remove: remove,
    postBlob: postBlob,
    downloadFile: downloadFile
}

async function post(endpoint, body, userHeaders, blockUI = true) {
    const headers = prepareHeaders(userHeaders)
    const globalStore = useGlobalStore()
    try {
        if (blockUI === true) {
            globalStore.toggleOn()
        }
        let response = await axios.post(`${config.SERVER_URL}:${config.SERVER_PORT}/${config.APP_PATH}${endpoint}`, body, {
            headers: headers
        })
        return new ServerResponse(response.data.message, response.data.result)
    } catch (error) {
        return handleRequestError(error)
    } finally {
        if (blockUI === true) {
            globalStore.toggleOff()
        }
    }
}

async function postBlob(endpoint, fileBlob, fileName, userHeaders) {
    const data = new FormData()
    data.append('file', fileBlob, fileName)
    let headers = {}
    if (userHeaders !== null && userHeaders !== undefined) {
        headers = userHeaders
    }
    headers['Content-Type'] = `multipart/form-data; boundary=${data._boundary}`
    return await post(endpoint, data, headers)
}

async function get(endpoint, userHeaders, blockUI = true) {
    const headers = prepareHeaders(userHeaders)
    const globalStore = useGlobalStore()
    try {
        if (blockUI === true) {
            globalStore.toggleOn()
        }
        let response = await axios.get(`${config.SERVER_URL}:${config.SERVER_PORT}/${config.APP_PATH}${endpoint}`, {
            headers: headers
        }) // , { httpsAgent: agent }
        return new ServerResponse(response.data.message, response.data.result)
    } catch (error) {
        return handleRequestError(error)
    } finally {
        if (blockUI === true) {
            globalStore.toggleOff()
        }
    }
}

async function downloadFile(endpoint, userHeaders) {
    axios({
        url: `${config.SERVER_URL}:${config.SERVER_PORT}/${config.APP_PATH}${endpoint}`,
        headers: prepareHeaders(userHeaders),
        method: 'GET',
        responseType: 'blob'
    }).then(response => {
        const fileName = response.headers['content-disposition'].split(';')[1].split('=')[1]
        if (!Capacitor.isNativePlatform()) {
            saveAs(response.data, fileName)
        } else {
            const reader = new FileReader()
            reader.onloadend = async () => {
                const result = await Filesystem.writeFile({
                    path: fileName,
                    data: reader.result,
                    directory: Directory.Documents
                })
                if (result.uri) {
                    pushLocalNotification('Downloaded: ' + fileName, result.uri)
                }
            }
            reader.readAsDataURL(response.data)
        }
    })
}

async function remove(endpoint, userHeaders, blockUI = true) {
    const headers = prepareHeaders(userHeaders)
    const globalStore = useGlobalStore()
    try {
        if (blockUI === true) {
            globalStore.toggleOn()
        }
        let response = await axios.delete(`${config.SERVER_URL}:${config.SERVER_PORT}/${config.APP_PATH}${endpoint}`, {
            headers: headers
        })
        return new ServerResponse(response.data.message, response.data.result)
    } catch (error) {
        return handleRequestError(error)
    } finally {
        if (blockUI === true) {
            globalStore.toggleOff()
        }
    }
}

function prepareHeaders(userHeaders) {
    let headers = {}
    if (userHeaders !== null && userHeaders !== undefined) {
        headers = userHeaders
    }
    if (headers['Accept-Language'] === undefined) {
        headers['Accept-Language'] = config.LOCALE
    }
    return headers
}

function handleRequestError(error) {
    let detail = ''
    let status
    let errorCode
    if (error.response) {
        // The client was given an error response (5xx, 4xx)        
        status = error.response.status
        detail = error.response.data.message
        errorCode = error.response.data.errorCode
    } else if (error.request) {
        // The client never received a response, and the request has never left
        detail = 'لا يوجد رد من الخادم'
    } else {
        // Anything else
        detail = 'حصل خطأ'
    }
    throw new ServerError(errorCode, detail, status)
}

async function pushLocalNotification(title, body) {
    const granted = await permissionsHelper.handleLocalNotification()
    if (granted === true) {
        const randomId = Math.floor(Math.random() * 2147483647);
        LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
            if (event.notification.id === randomId) {
                FileOpener.open({ filePath: event.notification.body })
            }
        })
        LocalNotifications.schedule(
            {
                notifications: [
                    {
                        id: randomId,
                        title: title,
                        body: body,
                        smallIcon: 'icon48'
                    }
                ]
            }
        )
    }
}

export default httpClientWrapper